import { cva } from 'class-variance-authority';
import {
  focusRing,
  transitionBase,
  uiMarker,
  uiMarkerInternal,
} from './shared';

/**
 * VIDEO PLAYER
 *
 * A video with its own controls, drawn on the dark `secondary` surface. The
 * prospect's sales video is the first one. Nothing here moves on its own: the
 * play button is still, and the only change on hover is a colour.
 */

/** The card the video and its controls sit in. */
export const videoPlayerRecipe = cva([
  uiMarker,
  'relative overflow-hidden rounded-3xl bg-secondary text-secondary-foreground shadow-e3',
]);

/** The 16:9 box the picture fills. */
export const videoPlayerStageRecipe = cva([
  uiMarkerInternal,
  'relative aspect-video w-full',
]);

export const videoPlayerMediaRecipe = cva([
  uiMarkerInternal,
  'size-full cursor-pointer bg-secondary object-cover',
]);

/** Laid over the picture until the video first plays. */
export const videoPlayerStartRecipe = cva([
  uiMarkerInternal,
  'absolute inset-0 flex flex-col items-center justify-center gap-4 bg-secondary/35 hover:bg-secondary/25',
  transitionBase,
  focusRing,
]);

/** The big round play button. Static: no pulse, no scale. */
export const videoPlayerStartGlyphRecipe = cva([
  uiMarkerInternal,
  'flex size-20 items-center justify-center rounded-pill bg-primary text-primary-foreground shadow-e2 md:size-24 [&_svg]:size-8 [&_svg]:translate-x-0.5 [&_svg]:fill-current md:[&_svg]:size-10',
]);

/** "Watch your video", under the play button. */
export const videoPlayerStartLabelRecipe = cva([
  uiMarkerInternal,
  'rounded-pill bg-card px-4 py-1.5 font-sans text-body-sm-strong text-card-foreground',
]);

/** The strip of controls under the picture. */
export const videoPlayerBarRecipe = cva([
  uiMarkerInternal,
  'flex flex-col gap-3 px-3 pb-4 pt-3 md:px-5',
]);

/** The seek bar's row: the drawn track with an invisible range input over it. */
export const videoPlayerSeekRecipe = cva([
  uiMarkerInternal,
  'relative flex h-5 items-center',
]);

export const videoPlayerTrackRecipe = cva([
  uiMarkerInternal,
  'relative h-1.5 w-full overflow-hidden rounded-pill bg-secondary-foreground/15',
]);

/** How far the video has got. */
export const videoPlayerFillRecipe = cva([
  uiMarkerInternal,
  'absolute inset-y-0 left-0 bg-card',
]);

export const videoPlayerThumbRecipe = cva([
  uiMarkerInternal,
  'pointer-events-none absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-pill bg-card shadow-e1',
]);

/** The real range input: full size, invisible, so it takes the pointer and the keyboard. */
export const videoPlayerRangeRecipe = cva([
  uiMarkerInternal,
  'absolute inset-0 w-full cursor-pointer appearance-none opacity-0',
]);

/** The row of buttons under the seek bar. */
export const videoPlayerControlsRecipe = cva([
  uiMarkerInternal,
  'flex items-center gap-1',
]);

/** Its right-hand end: the watched pill and full screen. */
export const videoPlayerControlsEndRecipe = cva([
  uiMarkerInternal,
  'ml-auto flex items-center gap-2',
]);

/** Play, mute, full screen. */
export const videoPlayerControlRecipe = cva([
  uiMarkerInternal,
  'flex size-9 shrink-0 items-center justify-center rounded-lg text-secondary-foreground hover:bg-secondary-foreground/10 [&_svg]:size-5',
  transitionBase,
  focusRing,
]);

export const videoPlayerTimeRecipe = cva([
  uiMarkerInternal,
  'ml-1 whitespace-nowrap font-sans text-body-sm tabular-nums text-secondary-foreground/70',
]);

/** "40% watched". */
export const videoPlayerWatchedRecipe = cva([
  uiMarkerInternal,
  'flex items-center gap-2 whitespace-nowrap rounded-pill bg-secondary-foreground/10 px-3 py-1 font-sans text-body-sm tabular-nums',
]);

/** The word "watched": read out always, shown from sm up, where the row has room. */
export const videoPlayerWatchedWordRecipe = cva([
  uiMarkerInternal,
  'sr-only sm:not-sr-only',
]);

/** The small bar inside the watched pill. Hidden on a phone, where the row is full. */
export const videoPlayerWatchedBarRecipe = cva([
  uiMarkerInternal,
  'relative hidden h-1.5 w-12 overflow-hidden rounded-pill bg-secondary-foreground/20 sm:block',
]);
